import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  getVaultRecords,
  createVaultRecord,
  updateVaultRecord,
  deleteVaultRecord,
  type VaultRecord,
  type VaultField,
} from "@/lib/api";
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  KeyRound,
  X,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

interface Props {
  accountId: string;
}

const EMPTY_FORM = {
  name: "",
  username: "",
  password: "",
  url: "",
  notes: "",
  fields: [] as VaultField[],
};

function CopyButton({ value, label }: { value: string; label: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast({ description: `${label} copied to clipboard` });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ description: "Copy failed", variant: "destructive" });
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Copy ${(label ?? "").toLowerCase()}`}
      className={`shrink-0 transition-colors p-1 rounded hover:bg-muted ${copied ? "text-green-500" : "text-slate-500 hover:text-foreground"}`}
    >
      <Copy className="w-4 h-4" />
    </button>
  );
}

function VaultRow({
  record,
  onEdit,
  onDelete,
}: {
  record: VaultRecord;
  onEdit: (r: VaultRecord) => void;
  onDelete: (r: VaultRecord) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const extraFields: VaultField[] = Array.isArray(record.fields) ? record.fields : [];

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Row header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Lock icon */}
        <div className="shrink-0 w-8 h-8 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center">
          <KeyRound className="w-4 h-4 text-emerald-600" />
        </div>

        {/* Name + toggle hint */}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium">{record.name ?? "(unnamed)"}</span>
          {!expanded && (
            <span className="ml-2 text-xs text-emerald-600 hover:underline">
              + Show information
            </span>
          )}
          {expanded && (
            <span className="ml-2 text-xs text-muted-foreground">
              − Hide information
            </span>
          )}
        </div>

        {/* Expand chevron */}
        <span className="shrink-0 text-muted-foreground">
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </span>

        {/* Edit / Delete */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(record); }}
          className="shrink-0 p-1.5 rounded border border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(record); }}
          className="shrink-0 p-1.5 rounded border border-red-200 bg-background text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t bg-muted/10 px-4 py-3 space-y-2.5 text-sm">
          {record.username && (
            <div className="flex items-center gap-2">
              <span className="w-20 text-xs font-semibold text-muted-foreground uppercase tracking-wide shrink-0">
                Username
              </span>
              <CopyButton value={record.username} label="Username" />
              <span className="flex-1 font-mono text-xs bg-background border rounded px-2 py-1 truncate">
                {record.username}
              </span>
            </div>
          )}

          {record.password && (
            <div className="flex items-center gap-2">
              <span className="w-20 text-xs font-semibold text-muted-foreground uppercase tracking-wide shrink-0">
                Password
              </span>
              <CopyButton value={record.password} label="Password" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowPassword((v) => !v); }}
                title={showPassword ? "Hide password" : "Reveal password"}
                className="shrink-0 p-1 rounded text-slate-500 hover:text-foreground hover:bg-muted transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <span className="flex-1 font-mono text-xs bg-background border rounded px-2 py-1 truncate">
                {showPassword ? record.password : "••••••••••••"}
              </span>
            </div>
          )}

          {record.url && (
            <div className="flex items-center gap-2">
              <span className="w-20 text-xs font-semibold text-muted-foreground uppercase tracking-wide shrink-0">
                Link
              </span>
              <CopyButton value={record.url} label="URL" />
              <a
                href={record.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline truncate min-w-0"
              >
                <ExternalLink className="w-3 h-3 shrink-0" />
                <span className="truncate">Launch in new tab</span>
              </a>
            </div>
          )}

          {/* Extra fields from FC vault */}
          {extraFields.map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-20 text-xs font-semibold uppercase tracking-wide shrink-0 truncate" title={f.label || "no label"}>
                {f.label
                  ? <span className="text-muted-foreground">{f.label}</span>
                  : <span className="text-amber-500 italic normal-case font-normal">no label</span>}
              </span>
              <CopyButton value={f.value ?? ""} label={f.label ?? ""} />
              <span className="flex-1 font-mono text-xs bg-background border rounded px-2 py-1 truncate">
                {f.value}
              </span>
            </div>
          ))}

          {record.notes && (
            <div className="flex items-start gap-2">
              <span className="w-20 text-xs font-semibold text-muted-foreground uppercase tracking-wide shrink-0 mt-0.5">
                Notes
              </span>
              <span className="flex-1 text-xs text-muted-foreground whitespace-pre-wrap">
                {record.notes}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AccountClientVault({ accountId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState<VaultRecord | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<VaultRecord | null>(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [searchQuery, setSearchQuery] = useState("");
  const [extraFieldLabel, setExtraFieldLabel] = useState("");
  const [extraFieldValue, setExtraFieldValue] = useState("");

  const { data: records = [], isLoading, isError, error } = useQuery({
    queryKey: ["vault", accountId],
    queryFn: () => getVaultRecords(accountId),
    retry: 1,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof EMPTY_FORM) =>
      createVaultRecord(accountId, {
        ...data,
        fields: data.fields,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vault", accountId] });
      setShowModal(false);
      setFormData({ ...EMPTY_FORM });
      toast({ description: "Vault record created" });
    },
    onError: (err: Error) => toast({ description: err.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof EMPTY_FORM }) =>
      updateVaultRecord(id, {
        ...data,
        fields: data.fields,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vault", accountId] });
      setShowModal(false);
      setEditRecord(null);
      setFormData({ ...EMPTY_FORM });
      toast({ description: "Vault record updated" });
    },
    onError: (err: Error) => toast({ description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteVaultRecord(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vault", accountId] });
      setDeleteRecord(null);
      toast({ description: "Vault record deleted" });
    },
    onError: (err: Error) => toast({ description: err.message, variant: "destructive" }),
  });

  const openAdd = () => {
    setEditRecord(null);
    setFormData({ ...EMPTY_FORM });
    setExtraFieldLabel("");
    setExtraFieldValue("");
    setShowModal(true);
  };

  const openEdit = (record: VaultRecord) => {
    setEditRecord(record);
    setFormData({
      name: record.name,
      username: record.username ?? "",
      password: record.password ?? "",
      url: record.url ?? "",
      notes: record.notes ?? "",
      fields: Array.isArray(record.fields) ? [...record.fields] : [],
    });
    setExtraFieldLabel("");
    setExtraFieldValue("");
    setShowModal(true);
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast({ description: "Name is required", variant: "destructive" });
      return;
    }
    if (editRecord) {
      updateMutation.mutate({ id: editRecord.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const addExtraField = () => {
    if (!extraFieldLabel.trim() || !extraFieldValue.trim()) return;
    setFormData((prev) => ({
      ...prev,
      fields: [...prev.fields, { label: extraFieldLabel.trim(), value: extraFieldValue.trim() }],
    }));
    setExtraFieldLabel("");
    setExtraFieldValue("");
  };

  const removeExtraField = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  const updateExtraField = (index: number, key: "label" | "value", val: string) => {
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.map((f, i) => i === index ? { ...f, [key]: val } : f),
    }));
  };

  const moveExtraField = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    setFormData((prev) => {
      const fields = [...prev.fields];
      [fields[index], fields[next]] = [fields[next], fields[index]];
      return { ...prev, fields };
    });
  };

  const filtered = records.filter((r) =>
    (r.name ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.username ?? "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center gap-3">
        <Input
          placeholder="Search by name or username"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-xs h-8 text-sm"
        />
        <div className="flex-1" />
        <Button size="sm" onClick={openAdd} className="gap-1">
          <Plus className="w-3.5 h-3.5" />
          + Record
        </Button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : isError ? (
        <div className="text-center py-12">
          <KeyRound className="w-8 h-8 mx-auto text-red-400/50 mb-2" />
          {(error as Error)?.message?.startsWith("401") ? (
            <>
              <p className="text-sm text-amber-600 font-medium">Session expired</p>
              <p className="text-xs text-muted-foreground mt-1">Please reload the page to continue.</p>
              <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="mt-3">
                Reload page
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-red-600 font-medium">Failed to load vault</p>
              <p className="text-xs text-muted-foreground mt-1">{(error as Error)?.message ?? "Unknown error"}</p>
            </>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <KeyRound className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">
            {searchQuery ? "No records match your search" : "No vault records yet"}
          </p>
          {!searchQuery && (
            <Button variant="outline" size="sm" onClick={openAdd} className="mt-3 gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add first record
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((record) => (
            <VaultRow
              key={record.id}
              record={record}
              onEdit={openEdit}
              onDelete={setDeleteRecord}
            />
          ))}
        </div>
      )}

      {/* Add / Edit modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) { setShowModal(false); setEditRecord(null); } }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editRecord ? "Edit Vault Record" : "New Encrypted Record"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. QuickBooks Online"
                className="mt-1 h-8 text-sm"
                autoFocus
              />
            </div>

            <div>
              <Label className="text-xs font-medium">Username</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData((p) => ({ ...p, username: e.target.value }))}
                placeholder="username or email"
                className="mt-1 h-8 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs font-medium">Password</Label>
              <Input
                type="text"
                value={formData.password}
                onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                placeholder="password"
                className="mt-1 h-8 text-sm font-mono"
              />
            </div>

            <div>
              <Label className="text-xs font-medium">URL / Link</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))}
                placeholder="https://..."
                className="mt-1 h-8 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs font-medium">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Any additional notes…"
                rows={2}
                className="mt-1 text-sm resize-none"
              />
            </div>

            {/* Custom fields */}
            {formData.fields.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Custom Fields</Label>
                {formData.fields.map((f, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <div className="flex flex-col shrink-0">
                      <button
                        type="button"
                        onClick={() => moveExtraField(i, -1)}
                        disabled={i === 0}
                        className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors"
                        title="Move up"
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveExtraField(i, 1)}
                        disabled={i === formData.fields.length - 1}
                        className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors"
                        title="Move down"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>
                    <Input
                      value={f.label}
                      onChange={(e) => updateExtraField(i, "label", e.target.value)}
                      placeholder="Label"
                      className="h-7 text-xs w-28 shrink-0"
                    />
                    <Input
                      value={f.value}
                      onChange={(e) => updateExtraField(i, "value", e.target.value)}
                      placeholder="Value"
                      className="h-7 text-xs flex-1 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => removeExtraField(i)}
                      className="shrink-0 text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add custom field row */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-muted-foreground">Add Custom Field</Label>
              <div className="flex gap-1.5">
                <Input
                  value={extraFieldLabel}
                  onChange={(e) => setExtraFieldLabel(e.target.value)}
                  placeholder="Label"
                  className="h-7 text-xs w-28 shrink-0"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtraField(); } }}
                />
                <Input
                  value={extraFieldValue}
                  onChange={(e) => setExtraFieldValue(e.target.value)}
                  placeholder="Value"
                  className="h-7 text-xs flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtraField(); } }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  onClick={addExtraField}
                  disabled={!extraFieldLabel.trim() || !extraFieldValue.trim()}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setShowModal(false); setEditRecord(null); }}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving…" : editRecord ? "Save Changes" : "Create Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteRecord} onOpenChange={(open) => { if (!open) setDeleteRecord(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete vault record?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteRecord?.name}" will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              onClick={() => deleteRecord && deleteMutation.mutate(deleteRecord.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
